// Client-side receipt builder: printable (PDF via print dialog) + .txt download.
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/461fdf84-6833-4ccd-88f7-5e84c5c415ef/images/6fb77a5d82eb531bd3113c598ca85d5c4e09f32a522bb077741965b1b3084bbb.png";
const PLAN_PRICES = { basic: 19, advanced: 49, free: 0 };
const PLAN_LABEL = {
  es: { basic: "Plan Básico", advanced: "Plan Avanzado", free: "Plan Gratis" },
  en: { basic: "Basic Plan", advanced: "Advanced Plan", free: "Free Plan" },
};

const L = {
  es: {
    title: "Recibo de pago",
    company: "RestauranteJobs",
    tagline: "Empleos en restaurantes mexicanos en EE. UU.",
    receiptNo: "Recibo #",
    date: "Fecha",
    billedTo: "Facturado a",
    concept: "Concepto",
    subscription: "Suscripción mensual",
    amount: "Monto",
    status: "Estado",
    paid: "Pagado",
    pending: "Pendiente",
    thanks: "¡Gracias por confiar en RestauranteJobs!",
    footer: "Este recibo es un comprobante de tu pago. Consérvalo para tus registros.",
    print: "Imprimir / Guardar PDF",
  },
  en: {
    title: "Payment receipt",
    company: "RestauranteJobs",
    tagline: "Jobs at Mexican restaurants in the USA",
    receiptNo: "Receipt #",
    date: "Date",
    billedTo: "Billed to",
    concept: "Item",
    subscription: "Monthly subscription",
    amount: "Amount",
    status: "Status",
    paid: "Paid",
    pending: "Pending",
    thanks: "Thanks for choosing RestauranteJobs!",
    footer: "This receipt is proof of your payment. Keep it for your records.",
    print: "Print / Save PDF",
  },
};

export const normalizeReceipt = (tx = {}, user = {}) => {
  const plan = tx.plan || "basic";
  const amount = tx.amount != null ? Number(tx.amount) : PLAN_PRICES[plan] ?? 0;
  let number;
  if (tx.receipt_number) {
    // Consecutive folio assigned by backend (e.g. RJ-0001) — use as-is.
    number = String(tx.receipt_number);
  } else {
    const rawId = tx.session_id || tx.id || `${Date.now()}`;
    number = "RJ-" + String(rawId).replace(/^cs_(test|live)_/, "").slice(-8).toUpperCase();
  }
  return {
    number,
    plan,
    amount,
    currency: (tx.currency || "usd").toUpperCase(),
    status: tx.payment_status || "paid",
    date: tx.created_at ? new Date(tx.created_at) : new Date(),
    name: tx.user_name || user.name || "—",
    email: tx.user_email || user.email || "—",
  };
};

const fmtMoney = (r) => `$${r.amount.toFixed(2)} ${r.currency}`;
const fmtDate = (d, lang) => d.toLocaleDateString(lang === "en" ? "en-US" : "es-MX", { year: "numeric", month: "long", day: "numeric" });

export const receiptText = (tx, user, lang = "es") => {
  const r = normalizeReceipt(tx, user);
  const s = L[lang] || L.es;
  const planLabel = (PLAN_LABEL[lang] || PLAN_LABEL.es)[r.plan] || r.plan;
  return [
    `${s.company} — ${s.title}`,
    "========================================",
    `${s.receiptNo}${r.number}`,
    `${s.date}: ${fmtDate(r.date, lang)}`,
    "",
    `${s.billedTo}: ${r.name} <${r.email}>`,
    "",
    `${s.concept}: ${planLabel} — ${s.subscription}`,
    `${s.amount}: ${fmtMoney(r)}`,
    `${s.status}: ${r.status === "paid" ? s.paid : s.pending}`,
    "========================================",
    s.thanks,
    s.footer,
  ].join("\n");
};

export const downloadReceiptTxt = (tx, user, lang = "es") => {
  const r = normalizeReceipt(tx, user);
  const blob = new Blob([receiptText(tx, user, lang)], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `recibo-restaurantejobs-${r.number}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

export const printReceipt = (tx, user, lang = "es") => {
  const r = normalizeReceipt(tx, user);
  const s = L[lang] || L.es;
  const planLabel = (PLAN_LABEL[lang] || PLAN_LABEL.es)[r.plan] || r.plan;
  const html = `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><title>${s.company} ${s.receiptNo}${r.number}</title>
  <style>
    *{box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}
    body{margin:0;background:#f5f3f0;color:#2d2422;padding:40px}
    .card{max-width:640px;margin:0 auto;background:#fff;border-radius:20px;overflow:hidden;border:1px solid #e7e0d8}
    .top{background:#d95a41;color:#fff;padding:28px 32px;display:flex;align-items:center;gap:16px}
    .top img{width:56px;height:56px;border-radius:12px;background:#fff;padding:2px;object-fit:contain}
    .top h1{margin:0;font-size:24px}.top p{margin:4px 0 0;opacity:.9;font-size:13px}
    .body{padding:32px}
    .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #f0ece6;font-size:14px}
    .row:last-child{border-bottom:0}
    .label{color:#8a817c}.val{font-weight:600;text-align:right}
    .total{display:flex;justify-content:space-between;margin-top:18px;padding-top:18px;border-top:2px solid #2d2422;font-size:20px;font-weight:700}
    .badge{display:inline-block;background:#e6f4ea;color:#22c55e;padding:4px 12px;border-radius:999px;font-size:12px;font-weight:700}
    .foot{padding:20px 32px;background:#faf8f5;color:#8a817c;font-size:12px;text-align:center}
    .thanks{font-weight:700;color:#d95a41;margin-bottom:6px}
    @media print{body{background:#fff;padding:0}.card{border:0}.noprint{display:none}}
  </style></head><body>
  <div class="card">
    <div class="top"><img src="${LOGO_URL}" alt="RestauranteJobs"/><div><h1>${s.company}</h1><p>${s.tagline}</p></div></div>
    <div class="body">
      <div class="row"><span class="label">${s.title}</span><span class="val">${s.receiptNo}${r.number}</span></div>
      <div class="row"><span class="label">${s.date}</span><span class="val">${fmtDate(r.date, lang)}</span></div>
      <div class="row"><span class="label">${s.billedTo}</span><span class="val">${r.name}<br>${r.email}</span></div>
      <div class="row"><span class="label">${s.concept}</span><span class="val">${planLabel} — ${s.subscription}</span></div>
      <div class="row"><span class="label">${s.status}</span><span class="val"><span class="badge">${r.status === "paid" ? s.paid : s.pending}</span></span></div>
      <div class="total"><span>${s.amount}</span><span>${fmtMoney(r)}</span></div>
    </div>
    <div class="foot"><div class="thanks">${s.thanks}</div>${s.footer}</div>
  </div>
  <div class="noprint" style="text-align:center;margin-top:24px">
    <button onclick="window.print()" style="background:#d95a41;color:#fff;border:0;padding:12px 24px;border-radius:12px;font-size:14px;font-weight:700;cursor:pointer">${s.print}</button>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
};

export { PLAN_PRICES };
